import { useEffect, useMemo, useState } from "react";
import { Gift, X, RefreshCw } from "lucide-react";

// Tema sazonal de Natal da tela de início.
// Ativo de 1º a 25 de dezembro (horário local do navegador).
// Para pré-visualizar fora do período: abrir /inicio?natal=1 (e ?natal=0 força desligado).
export function isTemaNatalAtivo(hoje = new Date()) {
  try {
    const param = new URLSearchParams(window.location.search).get("natal");
    if (param === "1") return true;
    if (param === "0") return false;
  } catch {
    /* ignora */
  }
  return hoje.getMonth() === 11 && hoje.getDate() >= 1 && hoje.getDate() <= 25;
}

// Presentes sem graça (de trabalho) que saem da caixa.
export const PRESENTES_NATAL = [
  { emoji: "🧦", titulo: "Uma meia velha", detalhe: "Só um pé. O outro está em análise no financeiro." },
  { emoji: "📋", titulo: "Mais demandas", detalhe: "Todas urgentes, todas para ontem, todas “rapidinhas”." },
  { emoji: "⏰", titulo: "Hora extra no dia 24", detalhe: "Fechamento não tira férias. Nem você." },
  { emoji: "📎", titulo: "Um clipe usado", detalhe: "Levemente torto. Ainda prende umas três folhas." },
  { emoji: "📊", titulo: "Uma planilha com #REF!", detalhe: "Ninguém sabe quem apagou a aba. Agora ela é sua." },
  { emoji: "🧾", titulo: "Uma nota fiscal sem centro de custo", detalhe: "Boa sorte descobrindo de quem é." },
  { emoji: "📅", titulo: "Reunião às 18h de sexta", detalhe: "“É rapidinho, só um alinhamento.”" },
  { emoji: "🏦", titulo: "Conciliação bancária de dezembro", detalhe: "Faltam R$ 0,03. Divirta-se." },
  { emoji: "📨", titulo: "999 e-mails não lidos", detalhe: "Metade com “conforme e-mail abaixo”." },
  { emoji: "✂️", titulo: "Um corte de 20% no orçamento", detalhe: "Mas mantendo todas as entregas, ok?" },
  { emoji: "🖊️", titulo: "Uma caneta sem tinta", detalhe: "Do brinde do fornecedor de 2019." },
  { emoji: "📝", titulo: "Um PDI em branco", detalhe: "Preencha até amanhã, por favor." },
  { emoji: "🔁", titulo: "Retrabalho", detalhe: "O cliente mudou de ideia. De novo." },
  { emoji: "🕐", titulo: "Banco de horas negativo", detalhe: "Presente do RH. Vem com juros." },
  { emoji: "☕", titulo: "Café requentado", detalhe: "Da garrafa térmica de segunda-feira." },
  { emoji: "📑", titulo: "Pedido de reembolso sem comprovante", detalhe: "“Mas eu juro que almocei.”" },
  { emoji: "🧮", titulo: "Uma calculadora sem a tecla 0", detalhe: "Para um orçamento mais enxuto." },
  { emoji: "📌", titulo: "Mais um ponto de atenção", detalhe: "Para a lista de pontos de atenção." },
  { emoji: "🔐", titulo: "Troca de senha obrigatória", detalhe: "Não pode repetir nenhuma das últimas 12." },
  { emoji: "📞", titulo: "Ligação de cobrança", detalhe: "Na hora do almoço, claro." },
  { emoji: "🗂️", titulo: "Uma pasta “Documentos_final_v3_AGORA_VAI”", detalhe: "Não é a versão final." },
  { emoji: "🎯", titulo: "Meta do ano que vem", detalhe: "O dobro desta. Com o mesmo time." },
  { emoji: "🧊", titulo: "Ar-condicionado no 17 ºC", detalhe: "Ninguém sabe onde está o controle." },
  { emoji: "📦", titulo: "Inventário de estoque no dia 26", detalhe: "Contar copo descartável é terapêutico." },
  { emoji: "🔌", titulo: "Um carregador que não serve em nada", detalhe: "Achado na gaveta do RH." },
  { emoji: "🗓️", titulo: "Férias remarcadas", detalhe: "Para depois do fechamento. Do ano que vem." },
  { emoji: "📈", titulo: "Um KPI novo", detalhe: "Para medir quanto tempo você gasta com KPIs." },
  { emoji: "🍞", titulo: "Panetone de frutas cristalizadas", detalhe: "O único que sobrou da confraternização." },
];

const CORES_LUZES = ["#ef4444", "#facc15", "#22c55e", "#3b82f6", "#ec4899"];

const ESTILOS = `
@keyframes natal-queda {
  0% { transform: translate3d(0, -10vh, 0); }
  100% { transform: translate3d(var(--natal-drift, 20px), 110vh, 0); }
}
@keyframes natal-pisca {
  0%, 100% { opacity: 1; filter: brightness(1.2); }
  50% { opacity: .35; filter: brightness(.7); }
}
@keyframes natal-balanca {
  0%, 100% { transform: rotate(-6deg); }
  50% { transform: rotate(6deg); }
}
@keyframes natal-sacode {
  0%, 100% { transform: rotate(0) scale(1); }
  20% { transform: rotate(-12deg) scale(1.05); }
  40% { transform: rotate(10deg) scale(1.08); }
  60% { transform: rotate(-8deg) scale(1.1); }
  80% { transform: rotate(6deg) scale(1.12); }
}
@keyframes natal-tampa {
  0% { transform: translateY(0) rotate(0); opacity: 1; }
  100% { transform: translateY(-70px) rotate(-25deg); opacity: 0; }
}
@keyframes natal-surge {
  0% { transform: scale(.85) translateY(10px); opacity: 0; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}
@keyframes natal-confete {
  0% { transform: translate(0, 0) rotate(0); opacity: 1; }
  100% { transform: translate(var(--cx), var(--cy)) rotate(540deg); opacity: 0; }
}
.natal-floco { position: absolute; top: 0; color: #fff; user-select: none;
  animation-name: natal-queda; animation-timing-function: linear; animation-iteration-count: infinite; }
.natal-luz { animation: natal-pisca 1.6s ease-in-out infinite; }
.natal-balanca { animation: natal-balanca 2.4s ease-in-out infinite; transform-origin: top center; }
.natal-sacode { animation: natal-sacode .9s ease-in-out; }
.natal-tampa-abre { animation: natal-tampa .5s ease-out forwards; }
.natal-surge { animation: natal-surge .45s ease-out both; }
.natal-confete { position: absolute; left: 50%; top: 45%; width: 8px; height: 12px; border-radius: 2px;
  animation: natal-confete 1.1s ease-out forwards; }
@media (prefers-reduced-motion: reduce) {
  .natal-floco, .natal-luz, .natal-balanca, .natal-sacode, .natal-tampa-abre, .natal-confete { animation: none !important; }
  .natal-floco { display: none; }
}
`;

export function EstilosNatal() {
  return <style>{ESTILOS}</style>;
}

// Neve caindo sobre a página inteira (não bloqueia cliques).
// `cor`/`sombra` permitem flocos visíveis também sobre fundo claro (ex.: login).
export function NeveNatal({ quantidade = 100, cor = "#fff", sombra = "0 0 4px rgba(255,255,255,.6)" }) {
  const flocos = useMemo(
    () =>
      Array.from({ length: quantidade }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 20 + Math.random() * 40,
        duration: 9 + Math.random() * 12,
        delay: -Math.random() * 20,
        opacity: 0.35 + Math.random() * 0.55,
        drift: Math.round(Math.random() * 80 - 40),
      })),
    [quantidade],
  );
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
      {flocos.map((f) => (
        <span
          key={f.id}
          className="natal-floco"
          style={{
            left: `${f.left}%`,
            fontSize: f.size,
            opacity: f.opacity,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
            "--natal-drift": `${f.drift}px`,
            color: cor,
            textShadow: sombra,
          }}
        >
          ❄
        </span>
      ))}
    </div>
  );
}

// Varal de pisca-pisca, para ficar logo abaixo do header.
export function PiscaPisca({ quantidade = 28 }) {
  return (
    <div aria-hidden="true" className="relative h-6 overflow-hidden bg-gradient-to-r from-purple-900 to-pink-800">
      <svg className="absolute inset-x-0 top-0 w-full h-3" preserveAspectRatio="none" viewBox="0 0 100 10">
        <path d="M0 2 Q 2.5 8 5 2 T 10 2 T 15 2 T 20 2 T 25 2 T 30 2 T 35 2 T 40 2 T 45 2 T 50 2 T 55 2 T 60 2 T 65 2 T 70 2 T 75 2 T 80 2 T 85 2 T 90 2 T 95 2 T 100 2"
          fill="none" stroke="#14532d" strokeWidth="0.6" />
      </svg>
      <div className="absolute inset-x-0 top-1 flex justify-around">
        {Array.from({ length: quantidade }, (_, i) => (
          <span
            key={i}
            className="natal-luz block w-2.5 h-3.5 rounded-full"
            style={{
              background: CORES_LUZES[i % CORES_LUZES.length],
              boxShadow: `0 0 8px ${CORES_LUZES[i % CORES_LUZES.length]}`,
              animationDelay: `${(i % 5) * 0.3}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function diasAteNatal(hoje = new Date()) {
  const natal = new Date(hoje.getFullYear(), 11, 25);
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((natal - inicioHoje) / 86400000);
}

// Hero festivo que substitui o de boas-vindas no período.
export function HeroNatal({ onAbrirPresente }) {
  const dias = diasAteNatal();
  const contagem =
    dias > 1 ? `Faltam ${dias} dias para o Natal` : dias === 1 ? "Amanhã é Natal!" : dias === 0 ? "Feliz Natal! Hoje é dia 25 🎄" : "Boas festas!";
  return (
    <div className="relative bg-gradient-to-r from-purple-900 via-rose-800 to-pink-800 text-white px-6 pb-10 pt-6 overflow-hidden">
      <div className="max-w-5xl mx-auto relative flex items-center justify-between gap-6">
        <div className="min-w-0">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-white/10 border border-white/20 rounded-full px-3 py-1">
          🎅 {contagem}
        </span>
        <h1 className="mt-3 text-3xl font-bold leading-tight">
          Boas festas no Painel Financeiro!
        </h1>
        <p className="mt-2 text-purple-100 text-sm max-w-xl">
          Que o seu Natal seja leve como uma conciliação que fecha de primeira.
          Selecione um módulo abaixo — ou abra um presente antes.
        </p>
        <button
          onClick={onAbrirPresente}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-rose-700 font-semibold text-sm shadow-lg hover:bg-rose-50 hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          <Gift className="w-4 h-4" />
          Abrir presente
        </button>
        </div>

        {/* Cena: árvore com boneco de neve e presentes, ao lado do texto */}
        <div aria-hidden="true" className="hidden sm:flex shrink-0 items-end gap-1 select-none pr-2">
          <span className="text-4xl mb-1 opacity-90">⛄</span>
          <span className="flex flex-col items-center">
            <span className="text-8xl leading-none natal-balanca drop-shadow-lg">🎄</span>
            <span className="-mt-3 text-2xl leading-none">🎁🎁</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function sortearPresente(anterior) {
  if (PRESENTES_NATAL.length < 2) return 0;
  let i;
  do {
    i = Math.floor(Math.random() * PRESENTES_NATAL.length);
  } while (i === anterior);
  return i;
}

const CORES_CONFETE = ["#ef4444", "#facc15", "#22c55e", "#ec4899", "#a855f7", "#ffffff"];

// Modal do presente: caixa fechada → sacode → abre → presente sem graça.
export function PresenteNatalModal({ aberto, onFechar }) {
  const [fase, setFase] = useState("fechado"); // fechado | sacudindo | aberto
  const [indice, setIndice] = useState(null);
  const [rodada, setRodada] = useState(0);

  useEffect(() => {
    if (aberto) {
      setFase("fechado");
    }
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return undefined;
    const onKey = (e) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto, onFechar]);

  const confetes = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => {
        const ang = (Math.PI * 2 * i) / 26 + Math.random() * 0.3;
        const dist = 90 + Math.random() * 90;
        return {
          id: `${rodada}-${i}`,
          cx: `${Math.cos(ang) * dist}px`,
          cy: `${Math.sin(ang) * dist - 40}px`,
          cor: CORES_CONFETE[i % CORES_CONFETE.length],
          delay: Math.random() * 0.15,
        };
      }),
    [rodada],
  );

  if (!aberto) return null;

  const abrir = () => {
    if (fase !== "fechado") return;
    setFase("sacudindo");
    setTimeout(() => {
      setIndice((ant) => sortearPresente(ant));
      setRodada((r) => r + 1);
      setFase("aberto");
    }, 900);
  };

  const outro = () => setFase("fechado");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onFechar}
      role="dialog"
      aria-modal="true"
      aria-label="Presente de Natal"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-rose-700 to-pink-800 text-white px-5 py-3 flex items-center justify-between">
          <span className="font-semibold text-sm flex items-center gap-2">
            <Gift className="w-4 h-4" /> Presente de Natal
          </span>
          <button onClick={onFechar} className="p-1 rounded hover:bg-white/15" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative px-6 py-8 min-h-[300px] flex flex-col items-center justify-center text-center">
          {fase !== "aberto" ? (
            <>
              <button
                onClick={abrir}
                disabled={fase !== "fechado"}
                className={`relative focus:outline-none ${fase === "sacudindo" ? "natal-sacode" : "hover:scale-105 transition-transform"}`}
                aria-label="Abrir o presente"
              >
                <CaixaPresente tampaAbrindo={fase === "sacudindo"} />
              </button>
              <p className="mt-5 text-sm text-gray-500">
                {fase === "sacudindo" ? "Abrindo…" : "Clique na caixa para abrir"}
              </p>
            </>
          ) : (
            <>
              {confetes.map((c) => (
                <span
                  key={c.id}
                  aria-hidden="true"
                  className="natal-confete"
                  style={{ background: c.cor, "--cx": c.cx, "--cy": c.cy, animationDelay: `${c.delay}s` }}
                />
              ))}
              <div className="natal-surge">
                <div className="text-6xl mb-3" aria-hidden="true">{PRESENTES_NATAL[indice].emoji}</div>
                <p className="text-xs font-semibold uppercase tracking-wider text-rose-600 mb-2">
                  Você ganhou
                </p>
                <p className="text-xl text-gray-900 font-bold leading-snug">
                  {PRESENTES_NATAL[indice].titulo}
                </p>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                  {PRESENTES_NATAL[indice].detalhe}
                </p>
              </div>
              <button
                onClick={outro}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-rose-700 to-pink-800 text-white text-sm font-semibold hover:opacity-90"
              >
                <RefreshCw className="w-4 h-4" /> Abrir outro presente
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CaixaPresente({ tampaAbrindo }) {
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" aria-hidden="true">
      {/* caixa */}
      <rect x="22" y="60" width="96" height="70" rx="6" fill="#be123c" />
      <rect x="62" y="60" width="16" height="70" fill="#facc15" />
      {/* tampa + laço */}
      <g className={tampaAbrindo ? "natal-tampa-abre" : ""} style={{ transformOrigin: "70px 50px" }}>
        <rect x="16" y="44" width="108" height="20" rx="5" fill="#e11d48" />
        <rect x="62" y="44" width="16" height="20" fill="#fde047" />
        <path d="M70 44 C 50 20, 30 34, 52 44 Z" fill="#facc15" />
        <path d="M70 44 C 90 20, 110 34, 88 44 Z" fill="#facc15" />
        <circle cx="70" cy="44" r="6" fill="#eab308" />
      </g>
    </svg>
  );
}
